/**
 * FindPwPage — A-2-3. 비밀번호 찾기
 * Step 1: 아이디 + 전화번호 → POST /member/find-pw/request (OTP 발송)
 * Step 2: 인증번호 입력 → POST /member/find-pw/verify → resetToken → /find-pw/change
 * 전화번호 11자리 blur → GET /member/users/sns-check → SNS 안내 다이얼로그
 */
import { useState, useEffect, useRef, useId } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Input } from '@indipia/ui-core';
import { AuthLayout } from '../../app/layouts/AuthLayout';
import { SnsAccountDialog } from '../components/SnsAccountDialog';
import { checkSns, findPwRequest, findPwVerify } from '../api/member-auth';

export function FindPwPage() {
  const navigate = useNavigate();
  const codeId   = useId();

  const [loginId,       setLoginId]       = useState('');
  const [mobile,        setMobile]        = useState('');
  const [code,          setCode]          = useState('');
  const [otpSent,       setOtpSent]       = useState(false);
  const [timer,         setTimer]         = useState(0);
  const [timerExpired,  setTimerExpired]  = useState(false);
  const [sendLoading,   setSendLoading]   = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [sendError,     setSendError]     = useState<string | null>(null);
  const [codeError,     setCodeError]     = useState<string | null>(null);
  const [snsDialog,     setSnsDialog]     = useState<{ types: string[] } | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  const formatTimer = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // 타이머 시작 (otpSent 토글 시)
  useEffect(() => {
    if (!otpSent) return;
    setTimer(180);
    setTimerExpired(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          setTimerExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSent]);

  // 전화번호 blur → SNS 체크
  const handleMobileBlur = async () => {
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 11) return;
    try {
      const sns = await checkSns({ mobile });
      if (sns.snsYn === 'Y') setSnsDialog({ types: sns.snsTypes });
    } catch { /* 무시 */ }
  };

  // 인증번호 발송
  const mobileDigits = mobile.replace(/\D/g, '');
  const canSend = loginId.trim().length > 0 && mobileDigits.length >= 11 && !sendLoading;

  const handleSend = async () => {
    if (!canSend) return;
    setSendLoading(true);
    setSendError(null);
    setCode('');
    setCodeError(null);
    setOtpSent(false); // 타이머 재시작 트리거
    try {
      await findPwRequest({ loginId: loginId.trim(), mobile });
      setOtpSent(true);
    } catch (e) {
      const errData  = (e as { response?: { data?: { code?: string; data?: { retryAfter?: number } } } })?.response?.data;
      const errCode  = errData?.code;
      const retryMin = Math.ceil((errData?.data?.retryAfter ?? 0) / 60);
      if (errCode === 'RATE_LIMITED') {
        setSendError(retryMin > 0
          ? `요청 한도를 초과했어요. 약 ${retryMin}분 후 다시 시도해 주세요.`
          : '요청 한도를 초과했어요. 잠시 후 다시 시도해 주세요.');
      } else if (errCode === 'TOO_MANY_ATTEMPTS') {
        setSendError('요청 횟수를 초과했습니다. 잠시 후 다시 시도해 주세요.');
      } else if (errCode === 'NOT_FOUND') {
        setSendError('입력하신 정보와 일치하는 계정이 없어요.');
      } else {
        setSendError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setSendLoading(false);
    }
  };

  // 인증번호 검증 → 비밀번호 변경 화면으로 이동
  const canVerify = otpSent && !timerExpired && code.length === 6 && !verifyLoading;

  const handleVerify = async () => {
    if (!canVerify) return;
    setVerifyLoading(true);
    setCodeError(null);
    try {
      const result = await findPwVerify({ loginId: loginId.trim(), mobile, verifyCode: code });
      if (intervalRef.current) clearInterval(intervalRef.current);
      navigate('/find-pw/change', { state: { loginId: loginId.trim(), resetToken: result.resetToken } });
    } catch (e) {
      const errCode = (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (errCode === 'CODE_MISMATCH') {
        setCodeError('인증번호가 일치하지 않습니다.');
      } else if (errCode === 'CODE_EXPIRED') {
        setCodeError('인증번호가 만료되었습니다. 다시 발송해 주세요.');
        setTimerExpired(true);
      } else {
        setCodeError('인증번호 확인에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const codeDisabled = !otpSent || timerExpired;

  return (
    <>
      <AuthLayout
        kicker="Reset Password"
        title="비밀번호 찾기"
        subtitle={<>아이디와 전화번호로 본인 확인 후<br />비밀번호를 변경할 수 있어요.</>}
        actions={
          <div className="flex flex-col gap-sm">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={!canVerify && !verifyLoading}
              loading={verifyLoading}
              onClick={() => void handleVerify()}
            >
              비밀번호 변경하기
            </Button>
            {/* 발송은 검증 전 단계의 필수 액션이라 primary — 초기 상태에선 위의 변경하기가
                disabled 라 강조되는 버튼은 이것 하나다 */}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              disabled={!canSend && !sendLoading}
              loading={sendLoading}
              onClick={() => void handleSend()}
            >
              {otpSent ? '재발송' : '인증번호 발송'}
            </Button>
            {/* 보조 강조 액션은 secondary(아웃라인) — D-09 §15.1 Variants */}
            <Button variant="secondary" size="lg" className="w-full" onClick={() => navigate('/login')}>
              로그인으로 돌아가기
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          <Input
            label="아이디"
            width="fill"
            size="lg"
            type="text"
            placeholder="아이디를 입력하세요"
            value={loginId}
            onChange={(e) => { setLoginId(e.target.value); setSendError(null); }}
          />

          <Input
            label="전화번호"
            width="fill"
            size="lg"
            type="tel"
            placeholder="010-0000-0000"
            value={mobile}
            onChange={(e) => { setMobile(formatPhone(e.target.value)); setSendError(null); }}
            onBlur={() => void handleMobileBlur()}
          />

          {sendError && <Alert kind="error">{sendError}</Alert>}

          {/*
            인증번호 필드 — 라벨 우측에 남은 시간이 붙는다. ui-core `Input` 의 `label` 은 string
            이라 슬롯을 낼 수 없어 `.field` 표준 클래스로 직접 조립한다(색·높이·포커스 링은
            그대로 토큰이 구동). `Input` 계약에 label 슬롯이 생기면 회수 대상이다.
          */}
          <div className={`field field-fill ${codeError ? 'error' : ''}`}>
            <div className="flex items-center justify-between">
              <label htmlFor={codeId}>인증번호</label>
              {otpSent && !timerExpired && (
                <span className={`text-caption font-bold ${timer < 30 ? 'text-danger' : 'text-point'}`}>
                  {formatTimer(timer)}
                </span>
              )}
            </div>
            <input
              id={codeId}
              className="input input-lg"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder={otpSent ? '인증번호 6자리' : '인증번호 발송 후 입력 가능합니다'}
              value={code}
              disabled={codeDisabled}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setCodeError(null); }}
            />
            {codeError && <span className="help">{codeError}</span>}
            {!codeError && timerExpired && otpSent && (
              <span className="text-caption text-text-secondary">인증번호가 만료되었습니다. 다시 발송해 주세요.</span>
            )}
            {!codeError && !otpSent && (
              <span className="text-caption text-text-disabled">인증번호 발송 후 입력 가능합니다</span>
            )}
          </div>
        </div>
      </AuthLayout>

      <SnsAccountDialog
        open={snsDialog != null}
        types={snsDialog?.types ?? []}
        onClose={() => setSnsDialog(null)}
        onContinue={() => setSnsDialog(null)}
        onGoLogin={() => { setSnsDialog(null); navigate('/login'); }}
      />
    </>
  );
}
